import {HoistModel, TaskObserver, XH, managed, persist} from '@xh/hoist/core';
import {action, bindable, observableRef} from '@xh/hoist/mobx';
import {DataViewModel} from '@xh/hoist/cmp/dataview';
import {GridModel} from '@xh/hoist/cmp/grid';
import {Store} from '@xh/hoist/data';
import {numberRenderer, percentRenderer, priceRenderer} from '@xh/hoist/format';
import {filler, hbox, span} from '@xh/hoist/cmp/layout';
import {button} from '@xh/hoist/desktop/cmp/button';
import {Icon} from '@xh/hoist/icon';
import {WatchList, WatchListUpdates} from '../../../../data/DataTypes';
import {itemRenderer} from './WatchlistItem';
import {WatchlistDialogModel} from './WatchlistDialogModel';
import {asServerException} from '../../../../../core/ServiceUtils';
import {NotificationMessage} from '../../../../data/MessageTypes';
import {WatchlistExpandedModel} from './WatchlistExpandedModel';
import {isTerminalStyle} from '../../../../cmp/UiStyle';
import {fileChartPieIcon} from '../../../../../core/Icons';

export class WatchlistPanelModel extends HoistModel {
    override persistWith = {prefKey: 'portfoliosTabState'};
    @bindable @persist accessor activeWatchListId: number;
    /** Two-line quote rows - used under the standard display style. */
    @managed dataViewModel: DataViewModel = isTerminalStyle() ? null : this.createDataViewModel();
    /** Dense ThinkOrSwim-style quote grid - used under the terminal display style. */
    @managed quoteGridModel: GridModel = isTerminalStyle() ? this.createQuoteGridModel() : null;
    @managed watchlistExpandedModel = new WatchlistExpandedModel();
    @observableRef accessor dialogModel = new WatchlistDialogModel(0);
    apiTask = TaskObserver.trackLast();

    get activeWatchListLabel() {
        return XH.watchListService.watchLists.find(it => it.value === this.activeWatchListId)?.label ?? '???';
    }

    constructor() {
        super();

        XH.messageHub.subscribe('watchlist', this.xhId, message => this.onWatchlistUpdate(message));

        this.addReaction({
            track: () => this.activeWatchListId,
            run: async watchListId => {
                await this.loadWatchlistAsync().linkTo(this.apiTask);
            },
            fireImmediately: true
        });
    }

    get store(): Store {
        return (this.quoteGridModel ?? this.dataViewModel).store;
    }

    get watchLists(): WatchList[] {
        return XH.watchListService.watchLists;
    }

    addList() {
        this.dialogModel = new WatchlistDialogModel(0);
        this.dialogModel.editAsync().then(() => this.refreshList());
    }

    @action
    editList() {
        this.dialogModel = new WatchlistDialogModel(this.activeWatchListId);
        this.dialogModel.editAsync().then(async () => this.refreshList());
    }

    showExpanded() {
        this.watchlistExpandedModel.show(this.activeWatchListId);
    }

    deleteList() {
        XH.confirm({
            title: 'Remove Watch List?',
            message: `Are you sure you want to remove the Watch List?`,
            confirmProps: {
                intent: 'danger',
                text: 'Remove Watch List'
            }
        }).then(async confirmed => {
            if (!confirmed) return;

            try {
                await this.deleteListAsync().linkTo(this.apiTask);
            } catch (e) {
                XH.handleException(asServerException(e), {title: 'Error deleting watchlist'});
            }
        });
    }

    sendToChart(ticker: string) {
        // `reload` so that re-sending the ticker already in the route still fires a transition -
        // the chart may have been pointed elsewhere since.
        XH.navigate('default.charts', {ticker}, {reload: true});
    }

    sendToResearch(ticker: string) {
        XH.navigate('default.research', {ticker});
    }

    private async loadWatchlistAsync() {
        if (!this.activeWatchListId) return;
        const watchList = await XH.watchListService.activateWatchlistAsync(this.activeWatchListId);
        this.store.loadData(watchList.updates.map(u => ({...u, prevPrice: u.price})));
    }

    private onWatchlistUpdate(message: NotificationMessage) {
        switch (message.envelope.eventType) {
            case 'UPDATE': {
                const watchlistUpdates = message.payload as WatchListUpdates;
                if (watchlistUpdates.watchlistId != this.activeWatchListId) return;
                this.updateWatchlist(watchlistUpdates);
                break;
            }
            case 'RELOAD':
                this.loadWatchlistAsync();
                break;
        }
    }

    private updateWatchlist(watchList: WatchListUpdates) {
        // Carry the last price along so the Last cell can flash up or down on this tick.
        const {store} = this;
        store.updateData(watchList.updates.map(u => ({...u, prevPrice: store.getById(u.ticker)?.data.price ?? u.price})));
    }

    private async deleteListAsync() {
        const newList = await XH.watchListService.deleteWatchlistAsync(this.activeWatchListId);
        if (newList) {
            this.activeWatchListId = newList.id;
        }
    }

    private async refreshList() {
        await XH.watchListService.loadAsync();
        await this.loadWatchlistAsync();
    }

    private createDataViewModel() {
        return new DataViewModel({
            store: {
                idSpec: 'ticker',
                fields: this.storeFields
            },
            itemHeight: 44,
            emptyText: 'no tickers',
            selModel: 'disabled',
            contextMenu: null,
            renderer: (v, {record}) => itemRenderer({model: this, record})
        });
    }

    private createQuoteGridModel() {
        const dirClass = {
            'rzx-dir--up': ({data: record}) => record.data.priceChange > 0,
            'rzx-dir--down': ({data: record}) => record.data.priceChange < 0
        };

        return new GridModel({
            store: {
                idSpec: 'ticker',
                fields: this.storeFields
            },
            sizingMode: 'tiny',
            emptyText: 'no tickers',
            selModel: 'single',
            showHover: true,
            rowBorders: true,
            stripeRows: false,
            colDefaults: {resizable: false, movable: false},
            onRowDoubleClicked: ({data: record}) => record && this.sendToChart(record.data.ticker),
            contextMenu: [
                {
                    text: 'Send to Charts',
                    icon: Icon.chartLine(),
                    actionFn: ({record}) => this.sendToChart(record.data.ticker)
                },
                {
                    text: 'Send to Research',
                    icon: fileChartPieIcon(),
                    actionFn: ({record}) => this.sendToResearch(record.data.ticker)
                }
            ],
            columns: [
                {
                    field: 'ticker',
                    headerName: 'Symbol',
                    width: 86,
                    cellClass: 'watchlist__quote-symbol',
                    cellClassRules: dirClass,
                    renderer: (v, {record}) => this.symbolCell(v, record.data.securityName)
                },
                {
                    field: 'price',
                    headerName: 'Last',
                    flex: 1,
                    minWidth: 58,
                    align: 'right',
                    renderer: priceRenderer({precision: 2}),
                    cellClassRules: {
                        ...dirClass,
                        'flash-green': ({data: record}) => record.data.price > record.data.prevPrice,
                        'flash-red': ({data: record}) => record.data.price < record.data.prevPrice
                    }
                },
                {
                    field: 'priceChange',
                    headerName: 'Chg',
                    width: 56,
                    align: 'right',
                    absSort: true,
                    renderer: numberRenderer({precision: 2, withPlusSign: true, colorSpec: true})
                },
                {
                    field: 'priceChangePct',
                    headerName: '%Chg',
                    width: 64,
                    align: 'right',
                    absSort: true,
                    // Colored text, not a solid fill - a fill here blends into the colored ticker cells beside the grid
                    renderer: percentRenderer({precision: 2, colorSpec: true})
                }
            ]
        });
    }

    /** Ticker with Chart and Research shortcuts, shown on row hover as in the standard rows. */
    private symbolCell(ticker: string, securityName: string) {
        return hbox({
            className: 'watchlist__quote-symbol-cell',
            items: [
                span({item: ticker, title: securityName ?? ticker}),
                filler(),
                button({
                    className: 'watchlist__quote-action',
                    icon: Icon.chartLine(),
                    minimal: true,
                    title: 'Send to Charts',
                    onClick: () => this.sendToChart(ticker)
                }),
                button({
                    className: 'watchlist__quote-action',
                    icon: fileChartPieIcon(),
                    minimal: true,
                    title: 'Send to Research',
                    onClick: () => this.sendToResearch(ticker)
                })
            ]
        });
    }

    private get storeFields() {
        return [
            {name: 'ticker'},
            {name: 'securityName'},
            {name: 'price', type: 'number' as const},
            {name: 'prevPrice', type: 'number' as const},
            {name: 'priceChange', type: 'number' as const},
            {name: 'priceChangePct', type: 'number' as const}
        ];
    }
}
